import React, { useState, useEffect, useRef } from "react";
import { useAPI } from "../../contexts/APIContext";
import { useAuth } from "../../contexts/AuthContext";
import { showToast } from "../Toast";
import logger from "../../utils/logger";
import { useAnalytics } from "../../hooks/useAnalytics";

/**
 * Reusable Like button for storybook and video content.
 * Props: contentType ("storybook" | "video"), contentId (uuid), size (optional), variant (optional: "pill" | "dropdown")
 */
const LikeButton = ({ contentType, contentId, size = "md", variant = "pill", onAfterClick }) => {
  const { apiClient } = useAPI();
  const { user } = useAuth();
  const { trackContentLiked } = useAnalytics();
  const [liked, setLiked] = useState(false);
  const [loading, setLoading] = useState(false);
  // Floating heart animations triggered on each "like" action
  const [hearts, setHearts] = useState([]); // [{ id, x, drift }]
  const heartIdRef = useRef(0);

  useEffect(() => {
    if (!contentId || !contentType || !user?.id) return;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await apiClient.checkLike(contentType, contentId);
        if (cancelled) return;
        if (res && res.success === false) return;
        const isLiked = res?.liked === true || res?.data?.liked === true;
        setLiked(!!isLiked);
      } catch (e) {
        if (!cancelled) logger.error("checkLike failed", e);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [contentId, contentType, user?.id, apiClient]);

  const handleClick = async () => {
    if (!user?.id) {
      showToast("Login to like", "error");
      onAfterClick?.();
      return;
    }
    if (!contentId || loading) return;

    onAfterClick?.(); // e.g. close dropdown immediately
    setLoading(true);
    const prev = liked;
    setLiked(!prev);
    trackContentLiked(contentType, contentId, !prev);

    // Burst of mini hearts shooting in different directions on each LIKE (not unlike).
    if (!prev) {
      const count = 6;
      const batchIds = [];
      for (let i = 0; i < count; i++) {
        const id = ++heartIdRef.current;
        batchIds.push(id);
        // Spread angles in a cone aimed upward: -110° to -70° from +x axis (i.e. up-left to up-right)
        const baseAngle = -90 - 40 + (80 * i) / (count - 1); // degrees
        const jitter = (Math.random() - 0.5) * 14;           // ±7°
        const angleDeg = baseAngle + jitter;
        const angleRad = (angleDeg * Math.PI) / 180;
        const distance = 38 + Math.random() * 22;            // 38–60px
        const dx = Math.cos(angleRad) * distance;
        const dy = Math.sin(angleRad) * distance;
        const size = 10 + Math.random() * 6;                  // 10–16px
        const duration = 700 + Math.random() * 350;           // 700–1050ms
        const rotate = (Math.random() - 0.5) * 60;            // ±30°
        setHearts((arr) => [...arr, { id, dx, dy, size, duration, rotate }]);
      }
      // Single timer covers the longest possible duration
      setTimeout(() => {
        setHearts((arr) => arr.filter((h) => !batchIds.includes(h.id)));
      }, 1200);
    }

    try {
      const res = liked
        ? await apiClient.unlikeContent(contentType, contentId)
        : await apiClient.likeContent(contentType, contentId);

      if (res && (res.success === false || res.error)) {
        setLiked(prev);
        const msg = String(res.error || "").toLowerCase();
        if (msg.includes("401") || msg.includes("unauthorized") || msg.includes("login")) {
          showToast("Login to like", "error");
        } else {
          logger.error("Like/unlike failed", res.error);
        }
      }
    } catch (err) {
      setLiked(prev);
      logger.error("Like/unlike failed", err);
    } finally {
      setLoading(false);
    }
  };

  const isDropdown = variant === "dropdown";
  // "crayon": the story page's chunky crayon buttons (pink, navy outline).
  const isCrayon = variant === "crayon";
  const baseClass = isDropdown
    ? "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
    : "flex items-center gap-2 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
  const sizeClass = isDropdown || isCrayon ? "" : size === "sm" ? "px-3 py-1.5" : "px-4 py-2";
  const styleClass = isDropdown
    ? ""
    : isCrayon
      ? `crayon-btn ${liked ? "bg-[#FF5C8F] text-white" : "bg-[#FF9CBE] text-[#0B1A33]"}`
    : liked
      ? "rounded-full bg-[#FFE9EE] border-2 border-[rgba(229,62,62,0.2)] text-[#E53E3E] shadow-[0px_3px_0px_rgba(229,62,62,0.12)]"
      : "rounded-full bg-white border-2 border-[rgba(0,101,248,0.12)] text-[#0B1A33] shadow-[0px_3px_0px_rgba(11,26,51,0.08)] hover:-translate-y-px hover:shadow-[0px_4px_0px_rgba(11,26,51,0.10)]";

  return (
    <span style={{ position: "relative", display: isDropdown ? "block" : "inline-block" }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={`${baseClass} ${sizeClass} ${styleClass}`}
        style={isDropdown ? { fontFamily: "'Fredoka', sans-serif", fontSize: 13, fontWeight: 700, color: liked ? "#E53E3E" : "#0B1A33" } : undefined}
        aria-label={liked ? "Liked" : "Like"}
      >
        {isCrayon ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill={liked ? "#FFFFFF" : "none"} stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s-7.5-4.6-9.8-9.1C.7 8.6 2.7 5 6.2 5c2 0 3.5 1 4.6 2.4l1.2 1.5 1.2-1.5C14.3 6 15.8 5 17.8 5c3.5 0 5.5 3.6 4 6.9C19.5 16.4 12 21 12 21z" />
          </svg>
        ) : isDropdown ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill={liked ? "#E53E3E" : "none"} stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s-7.5-4.6-9.8-9.1C.7 8.6 2.7 5 6.2 5c2 0 3.5 1 4.6 2.4l1.2 1.5 1.2-1.5C14.3 6 15.8 5 17.8 5c3.5 0 5.5 3.6 4 6.9C19.5 16.4 12 21 12 21z" />
          </svg>
        ) : (
          <span className="text-lg">{liked ? "❤️" : "🤍"}</span>
        )}
        {liked ? "Liked" : "Like"}
      </button>

      {/* Mini-heart burst: SVG hearts shoot out from the button on each like */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        {hearts.map((h) => (
          <span
            key={h.id}
            className="lb-heart"
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: h.size,
              height: h.size,
              transform: "translate(-50%, -50%)",
              // CSS variables consumed by the keyframes
              "--lb-dx": `${h.dx}px`,
              "--lb-dy": `${h.dy}px`,
              "--lb-rot": `${h.rotate}deg`,
              animationDuration: `${h.duration}ms`,
              willChange: "transform, opacity",
              filter:
                "drop-shadow(0 2px 4px rgba(229,62,62,0.45)) drop-shadow(0 0 6px rgba(229,62,62,0.30))",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="100%"
              height="100%"
              fill="#E53E3E"
              aria-hidden="true"
            >
              <path d="M12 21s-7.5-4.6-9.8-9.1C.7 8.6 2.7 5 6.2 5c2 0 3.5 1 4.6 2.4l1.2 1.5 1.2-1.5C14.3 6 15.8 5 17.8 5c3.5 0 5.5 3.6 4 6.9C19.5 16.4 12 21 12 21z" />
            </svg>
          </span>
        ))}
      </span>

      <style>{`
        .lb-heart {
          animation-name: lb-heart-burst;
          animation-timing-function: cubic-bezier(.22, 1, .36, 1);
          animation-fill-mode: forwards;
        }
        @keyframes lb-heart-burst {
          0% {
            transform: translate(-50%, -50%) translate(0, 0) scale(0.4) rotate(0deg);
            opacity: 0;
          }
          12% {
            transform: translate(-50%, -50%) translate(calc(var(--lb-dx) * 0.15), calc(var(--lb-dy) * 0.15)) scale(1.05) rotate(calc(var(--lb-rot) * 0.2));
            opacity: 1;
          }
          70% {
            transform: translate(-50%, -50%) translate(calc(var(--lb-dx) * 0.85), calc(var(--lb-dy) * 0.85)) scale(0.95) rotate(calc(var(--lb-rot) * 0.85));
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) translate(var(--lb-dx), var(--lb-dy)) scale(0.55) rotate(var(--lb-rot));
            opacity: 0;
          }
        }
      `}</style>
    </span>
  );
};

export default LikeButton;
